import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from 'config/axiosConfig';
import { transformHtmlContent } from 'util/transformHtmlContent';
import EntityActions from 'components/Entities/EntityActions';
import Report from 'components/Entities/Report';
import { ChevronsDown, ChevronsUp, Ellipsis, Flag, Pencil, Trash, Clock } from 'lucide-react';
import { Chip, Tooltip } from '@mui/joy';
import { Feedback } from 'util/constants';
import { formatDate, formatRelativeTime } from 'config/dayjsConfig';
import { useUser } from 'util/UserContext';
import EntityAvatar from 'components/Entities/EntityAvatar';
import { 
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'ui/DropdownMenu';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from 'ui/Carousel';
import { Drawer, DrawerContent, DrawerTrigger } from 'ui/Drawer';
import DialogConfirmation from 'ui/DialogConfirmation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'ui/InputSelect';
import TextEditor from 'components/TextEditor';
import InputTextArea from 'ui/InputTextArea';
import EntityPrivacy from 'components/Entities/EntityPrivacy';
import { PrivacyLevel } from 'util/userUtils';
import CoauthorSelect, { CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import { loadCoauthorData, handleCoauthorSelect as selectCoauthor, handleCoauthorRemove as removeCoauthor } from 'util/entityUtils';
import DetailPanel from 'ui/DetailPanel';

interface Props {
  feedback: Feedback;
  showMeta?: boolean;
  truncateDescription?: boolean;
  isFeedbackProfilePage?: boolean;
  showActions?: boolean;
  onVote?: () => void;
  editMode?: boolean;
  onTitleChange?: (value: string) => void;
  onDescriptionChange?: (value: string) => void;
  onCategoryChange?: (value: Feedback['properties']['category']) => void;
  onPrivacyChange?: (value: PrivacyLevel) => void;
  onStatusChange?: (value: Feedback['status']) => void;
  onCoauthorsChange?: (addedCoauthors: CoauthorSuggestion[], removedCoauthors: CoauthorSuggestion[]) => void;
  titleValidationMessage?: string;
  descriptionValidationMessage?: string;
  children?: React.ReactNode;
}

const FeedbackCard = ({
  feedback,
  showMeta = true,
  truncateDescription = true,
  isFeedbackProfilePage = false,
  editMode = false,
  onTitleChange,
  onDescriptionChange,
  onCategoryChange,
  onPrivacyChange,
  onStatusChange,
  onCoauthorsChange,
  titleValidationMessage,
  descriptionValidationMessage,
  children,
}: Props) => {
  const { currentUser, setShowAuthModal, userIsMobile } = useUser();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const feedbackHref = `/feedback/${encodeURIComponent(feedback.id)}`;
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const TruncateMarkup = require('react-truncate-markup').default;
  const [existingCoauthors, setExistingCoauthors] = useState<CoauthorSuggestion[]>(feedback?.properties?.coauthors || []);
  const [newCoauthors, setNewCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [removedCoauthors, setRemovedCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [pendingCoauthors, setPendingCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [isLoadingCoauthors, setIsLoadingCoauthors] = useState<boolean>(false);
  const [privacy, setPrivacy] = useState<PrivacyLevel>((feedback.properties?.privacy as PrivacyLevel) || 'public');

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const element = event.target as HTMLElement;
    if (element.tagName.toLowerCase() === 'a') return;    
    if (element.classList.contains('read-more')) return;
    if (!isFeedbackProfilePage) {
      navigate(feedbackHref);
    } else {
      return;
    }
  };

  const handleDescriptionClick = (event: React.MouseEvent) => {
    if (!truncateDescription) return;
    event.preventDefault();
    if (isFeedbackProfilePage) {
      setIsExpanded(!isExpanded);
    } else {
      return;
    }
  };

  const handleReadMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsExpanded(!isExpanded);
    event.stopPropagation();
  };

  const handleReportClick = () => {
    if (!currentUser) {
      setShowAuthModal(true);
    } else {
      setMenuOpen(false);
      setDrawerOpen(false);
      setIsReportModalOpen(true);
    }
  };

  const handleEdit = () => {
    setMenuOpen(false);
    setDrawerOpen(false);
    navigate(`/feedback/${feedback.id}?edit=true`);
  };

  const handleDelete = () => {
    setMenuOpen(false);
    setDrawerOpen(false);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/feedbacks/${feedback.id}`);
      setIsDeleteDialogOpen(false);
      if (isFeedbackProfilePage) {
        navigate('/');
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
    }
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
  };

  const MoreButton = React.forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement>
  >((props, ref) => (
    <button
      type="button"
      ref={ref}
      {...props}
      className="card-more size-7 rounded-full flex justify-center items-center"
    >
      <Ellipsis className="icon-stroke size-5"/>
    </button>
  ))
  MoreButton.displayName = 'MoreButton'

  const MoreOptions = () => (
    <>
      {!userIsMobile ? (
      <div className="flex flex-col">
      {currentUser?.id === feedback.user_id && (
        <>
          <DropdownMenuItem onClick={handleEdit}>
            <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
            <Pencil className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
              <span>Edit</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDelete}>
            <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
            <Trash className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
              <span>Delete</span>
            </div>
          </DropdownMenuItem>
        </>
      )}
      <DropdownMenuItem onClick={handleReportClick}>
        <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
          <Flag className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
          <span>Report</span>
        </div>
      </DropdownMenuItem>
      </div>
      ) : (
        <>
          {currentUser?.id === feedback.user_id && (
            <>
              <button onClick={handleEdit} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                  <Pencil className="size-4"/>
                  <span>Edit</span>
              </button>
              <button onClick={handleDelete} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                  <Trash className="size-4"/>
                  <span>Delete</span>
              </button>
            </>
          )}
          <>
            <button onClick={handleReportClick} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                <Flag className="size-4"/>
                <span>Report</span>
            </button>
          </>
        </>
      )}  
    </>
  )

  const getChipColor = (
    status: Feedback['status']
    ) => {
        switch (status) {
            case 'Open':
                return '#cf1f1f'
            case 'Reviewing':
                return '#005286'
            case 'Planned':
                return '#df7023'
            case 'Unplanned':
                return '#cf1f1f'
            case 'Developing':
                return '#8e00a7'
            case 'Released':
                return '#419357'
            case 'Closed':
                return '#666666'
            default:
                return '#555555' // Fallback color
        }
    }

      
    const truncateEllipsis = (node: any) => {
      return (
        <span className="read-more">
        <button 
          className="read-more-border w-full text-sm rounded-md border-b-2 justify-center items-center px-1"
          onClick={handleReadMoreClick}
        >
        <span className="flex justify-center items-center ">
            <ChevronsDown className="flex justify-center items-center"/>
            </span>
          </button>
            </span>
      );
    };

    const renderDescription = () => {
      if (truncateDescription && !isExpanded) {
        return (
        <TruncateMarkup lines={10} tokenize="words" ellipsis={truncateEllipsis}>
          <div>
            {transformHtmlContent(feedback.description || '')}
          </div>
        </TruncateMarkup>
        );
      } else if (truncateDescription && isExpanded) {
          return (
              <div>
                  {transformHtmlContent(feedback.description || '')}
                  <span className="flex">
                    <button 
                      className="read-more-border w-full text-sm rounded-md border-b-2 justify-center items-center px-1"
                      onClick={handleReadMoreClick}
                    >
                      <span className="flex justify-center items-center ">
                        <ChevronsUp className="flex justify-center items-center" />
                      </span>
                    </button>
                  </span>
                </div>
            );
      } else {
        return transformHtmlContent(feedback.description || '');
      }
    };

    useEffect(() => {
      if (feedback.id && currentUser?.id === feedback.user_id) {
        loadCoauthorData(
          {
            id: feedback.id,
            user_id: feedback.user_id,
            properties: {
              coauthors: feedback.properties?.coauthors || []
            }
          },
          'feedback',
          currentUser?.id,
          {
            setExistingCoauthors,
            setPendingCoauthors,
            setIsLoadingCoauthors
          }
        );
      }
    }, [feedback.id, feedback.user_id, feedback.properties?.coauthors, currentUser?.id]);

    const handleCoauthorSelect = (suggestion: CoauthorSuggestion) => {
      selectCoauthor(
        suggestion,
        existingCoauthors,
        newCoauthors,
        setNewCoauthors,
        onCoauthorsChange,
        removedCoauthors
      );
    };

    const handleCoauthorRemove = async (coauthor: CoauthorSuggestion) => {
      await removeCoauthor(
        coauthor,
        {
          setNewCoauthors,
          setRemovedCoauthors,
          setPendingCoauthors,
          setExistingCoauthors
        },
        {
          newCoauthors,
          removedCoauthors
        },
        onCoauthorsChange
      );
    };

    // Handle privacy change for impact
    const handlePrivacyChange = (newPrivacy: PrivacyLevel) => {
      setPrivacy(newPrivacy);
      onPrivacyChange?.(newPrivacy);
      
      // No need to call API here as the EntityPrivacy component handles the API call in edit mode
      // This just updates our local state to stay in sync
      
      // Also update the impact object's properties to reflect the new privacy setting
      if (feedback.properties) {
        feedback.properties.privacy = newPrivacy;
      } else {
        feedback.properties = { privacy: newPrivacy };
      }
    };

    return (
    <div className="card-container my-1">
      {isReportModalOpen && (
        <Report
          entityId={feedback.id}
          entityType="feedback"
          open={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
        />
      )}
      <article>
        <div className={`rounded-lg flex flex-col px-2 md:px-3 py-2 relative ${isFeedbackProfilePage ? 'card-main-profile-page' : 'card-main'}`}>
          <div className="entity-actions-container order-last">
            <EntityActions entity={feedback} entityType="feedback" />
          </div>
          <div className="card-content order-first">
            {showMeta && (
              <div className="card-meta text-sm flex items-center justify-between relative">
                <EntityAvatar 
                  entity={feedback}
                  entityType="feedback"
                  isEntityProfilePage={isFeedbackProfilePage}
                  isEntitySection={!isFeedbackProfilePage}
                  showLink={true}
                />
                <div className="icon-more text-xl">
                  {!editMode && (userIsMobile ? (
                    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                      <DrawerTrigger asChild>
                        <MoreButton />
                      </DrawerTrigger>
                      <DrawerContent title="Actions">
                        <MoreOptions />
                      </DrawerContent>
                    </Drawer>
                  ) : (
                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                      <DropdownMenuTrigger asChild>
                        <MoreButton />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <MoreOptions />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ))}
                </div>
              </div>
            )}
            <div
              className="card-header mt-1"
              onClick={!editMode ? handleCardClick : undefined}
            >
              {editMode ? (
                <div className="form-control my-1">
                  <InputTextArea
                    id="feedback-title"
                    name="feedback-title"
                    labelText="Title"
                    fieldRequired
                    value={feedback.title}
                    onChange={(e) => onTitleChange?.(e.target.value)}
                    autoComplete={`feedback-title`}
                    maxLength={300}
                    error={titleValidationMessage}
                    validationState={titleValidationMessage ? 'invalid' : !titleValidationMessage && feedback.title && feedback.title.length > 0 ? 'valid' : 'none'}
                  />
                </div>
              ) : isFeedbackProfilePage ? (
                <span className="card-title">
                  {feedback.title}
                </span>
              ) : (
                <Link to={feedbackHref} className="card-title">
                  {feedback.title}
                </Link>
              )}
              <div className="card-tags flex flex-row flex-wrap gap-2 my-1 items-center">
                <Chip
                  component={'div' as any}
                  label="Feedback"
                  color="neutral"
                  variant="soft"
                  size="md"
                  sx={{
                    backgroundColor: '#123456',
                    color: '#fff',
                    fontSize: '0.75rem',
                  }}
                >
                  Feedback
                </Chip>
                {editMode ? (
                  <Select 
                    onValueChange={onCategoryChange} 
                    value={feedback.properties.category}
                    required
                    aria-required="true"
                  >
                    <SelectTrigger className="entity-filter-select">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bug">Bug</SelectItem>
                      <SelectItem value="Enhancement">Enhancement</SelectItem>
                      <SelectItem value="Functionality">Functionality</SelectItem>
                      <SelectItem value="User Interface (UI)">User Interface (UI)</SelectItem>
                      <SelectItem value="User Experience (UX)">User Experience (UX)</SelectItem>
                      <SelectItem value="Performance">Performance</SelectItem>
                      <SelectItem value="Compliance">Compliance</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                ) : feedback.properties?.category && (
                  <Chip
                    component={'div' as any}
                    label={feedback.properties.category}
                    color="neutral"
                    variant="soft"
                    size="md"
                    sx={{
                      backgroundColor: '#666666',
                      color: '#fff',
                      fontSize: '0.75rem',
                    }}
                  >
                    {feedback.properties.category}
                  </Chip>
                )}
                {editMode && currentUser?.is_admin ? (
                  <Select 
                    onValueChange={onStatusChange} 
                    value={feedback.status}
                    required
                    aria-required="true"
                  >
                    <SelectTrigger className="entity-filter-select">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Open">Open</SelectItem>
                      <SelectItem value="Reviewing">Reviewing</SelectItem>
                      <SelectItem value="Planned">Planned</SelectItem>
                      <SelectItem value="Unplanned">Unplanned</SelectItem>
                      <SelectItem value="Developing">Developing</SelectItem>
                      <SelectItem value="Released">Released</SelectItem>
                      <SelectItem value="Closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                ) : feedback.status && (
                  <Chip
                    component={'div' as any}
                    label={feedback.status}
                    color="primary"
                    variant="soft"
                    size="md"
                    sx={{
                      backgroundColor: getChipColor(feedback.status),
                      color: '#fff',
                      fontSize: '0.75rem',
                    }}
                  >
                    {feedback.status}
                  </Chip>
                )}
                <EntityPrivacy
                  privacy={feedback.properties.privacy}
                  viewerRelation={feedback.viewer_relation}
                  onPrivacyChange={() => {}}
                  actorType="user"
                  editMode={false}
                  entityId={feedback.id}
                  entityType="feedback"
                  miniIcon={true}
                  showDropdown={false}
                />
                <div className="flex items-center">
                  <Tooltip title={formatDate(feedback.created_at)} arrow>
                    <span className="card-date font-normal">
                      {formatRelativeTime(feedback.created_at)}
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className="flex flex-col-reverse md:flex-row gap-2">
              {editMode ? (
                <div className="w-full space-y-2">
                <DetailPanel 
                title={`Collaborators (${existingCoauthors.length + pendingCoauthors.length})`} 
                className="mt-1 mb-2 text-sm bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
                defaultOpen={true}>
                  {isLoadingCoauthors ? (
                    <div className="p-2 text-center">Loading collaborators...</div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex flex-col gap-2">
                        <CoauthorSelect
                          inputType="add"
                          placeholder="Add collaborators"
                          className="w-full"
                          onSelect={handleCoauthorSelect}
                        />
                      </div>
                      {existingCoauthors.map((coauthor) => (
                        <EntityAvatar
                          key={`existing-${coauthor.type}-${coauthor.id}`}
                          entity={coauthor as any}
                          entityType={coauthor.type}
                          entityChip={true}
                          showCloseChip={editMode}
                          isInputField={false}
                          showLink={false}
                          onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                        />
                      ))}
                      {pendingCoauthors.map((coauthor) => (
                        <EntityAvatar
                          key={`pending-${coauthor.type}-${coauthor.id}`}
                          entity={coauthor as any}
                          entityType={coauthor.type}
                          entityChip={true}
                          showCloseChip={editMode}
                          isInputField={false}
                          showLink={false}
                          badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                          onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                        />
                      ))}
                      {newCoauthors.map((coauthor) => (
                        <EntityAvatar
                          key={`new-${coauthor.type}-${coauthor.id}`}
                          entity={coauthor as any}
                          entityType={coauthor.type}
                          entityChip={true}
                          showCloseChip={editMode}
                          isInputField={false}
                          showLink={false}
                          badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                          onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                        />
                      ))}
                    </div>
                  )}
              </DetailPanel>
                  <TextEditor
                    value={feedback.description}
                    onChange={(content: string) => onDescriptionChange?.(content)}
                    editorType="entity"
                    placeholder="Description"
                    fieldRequired
                    action="edit"
                    isEditorExpanded={true}
                  >
                  {descriptionValidationMessage && (
                    <div className="text-warning-red dark:text-dark-warning-red text-sm mt-1">
                      {descriptionValidationMessage}
                    </div>
                  )}
                  </TextEditor>
                </div>
              ) : (
                <>
                <div className="card-description" onClick={handleCardClick}>
                  <span onClick={handleDescriptionClick}>
                    {renderDescription()}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(existingCoauthors.length || pendingCoauthors.length) > 0 &&
                    <DetailPanel 
                      title={`Collaborators (${existingCoauthors.length + pendingCoauthors.length})`} 
                      className="mt-1 text-sm bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal">
                      <Carousel className="border-none border-0">
                        <div className="flex justify-between ">
                          <>
                            <CarouselContent>
                                {existingCoauthors.map((coauthor) => (
                                  <CarouselItem key={`existing-${coauthor.type}-${coauthor.id}`} className="border-none basis-auto">
                                    <EntityAvatar
                                      key={`existing-${coauthor.type}-${coauthor.id}`}
                                      entity={coauthor as any}
                                      entityType={coauthor.type}
                                      entityChip={true}
                                      showCloseChip={false}
                                      isInputField={false}
                                      showLink={true}
                                    />
                                  </CarouselItem>
                                ))}
                                {pendingCoauthors.map((coauthor) => (
                                  <CarouselItem key={`pending-${coauthor.type}-${coauthor.id}`} className="border-none basis-auto">
                                    <EntityAvatar
                                      key={`pending-${coauthor.type}-${coauthor.id}`}
                                      entity={coauthor as any}
                                      entityType={coauthor.type}
                                      entityChip={true}
                                      showCloseChip={false}
                                      isInputField={false}
                                      badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                                      showLink={true}
                                    />
                                  </CarouselItem>
                                ))}
                            </CarouselContent>
                            <div className="flex flex-col justify-between">
                              <CarouselNext className="flex" size={'circle'}/>
                              <CarouselPrevious className="flex" size={'circle'}/>
                            </div>
                          </>
                        </div>
                      </Carousel>
                    </DetailPanel>
                  }
                </div>
                </>
              )}
            </div>
            {editMode && (
              <div className="flex flex-wrap w-full justify-between items-center gap-2 mt-2">
                <div className="flex-shrink-0">
                  <EntityPrivacy
                    privacy={privacy}
                    onPrivacyChange={handlePrivacyChange}
                    editMode={true}
                    entityId={feedback.id}
                    entityType="feedback"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  {children}
                </div>
              </div>
            )}
          </div>
        </div>
      </article>      
      {isDeleteDialogOpen && (
        <DialogConfirmation 
          title="Delete Feedback" 
          description="Are you sure you want to delete this feedback?" 
          isOpen={isDeleteDialogOpen} 
          onConfirm={confirmDelete} 
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

export default FeedbackCard;